// The in-app connection state.
//
// The offline PAGE is what the browser shows when a navigation fails. This is
// the other half: what an already-open app says when requests start failing
// underneath somebody still looking at a working-looking screen. Without it,
// the only symptom is that things quietly stop happening.
//
// ⚠️ Deliberately NOT a modal and NOT dismissible-with-consequence: it reports
// a condition, it does not ask for a decision, and it must never sit between
// somebody and the save button they are trying to press.
import React, { useEffect, useState } from 'react';
import { CloudOff, WifiOff } from 'lucide-react';
import { connectionMessage } from './connectionState';
import { connectionSnapshot, subscribeToConnection } from './connectionSignal';

export const ConnectionBanner: React.FC = () => {
  const [state, setState] = useState(connectionSnapshot);

  useEffect(() => {
    const sync = () => setState(connectionSnapshot());
    sync();
    return subscribeToConnection(sync);
  }, []);

  const message = connectionMessage(state);
  if (!message) return null;

  return (
    <div
      // role=status, not alert: this is a condition worth knowing, not an
      // interruption. aria-live polite so a screen reader finishes the
      // sentence it is on rather than cutting across someone mid-task.
      role="status"
      aria-live="polite"
      data-testid="connection-banner"
      data-state={state}
      className="fixed bottom-4 left-4 z-40 w-[min(24rem,calc(100vw-2rem))] rounded-xl border border-border bg-card p-3 shadow-lg"
    >
      <div className="flex items-start gap-3">
        {state === 'offline' ? (
          <WifiOff className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden />
        ) : (
          <CloudOff className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">{message.title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{message.detail}</p>
        </div>
      </div>
    </div>
  );
};
